// src/services/hap/HapApiServiceImpl.ts
// HAP HTTP API client implementation for browser

import type {
  HapApiService,
  HapApiConfig,
  FileAnnouncementRequest,
  FileAnnouncementResponse
} from '@/services/abstract/HapApiService';
import type { LoggerService } from '@/services/abstract/LoggerService';

const HEALTH_CHECK_TIMEOUT_MS = 5000;
const ANNOUNCE_TIMEOUT_MS = 30000;

/**
 * Generate auth token from swarm key using SHA-256
 * This must match the algorithm used by the HAP server
 */
async function generateAuthToken(swarmKey: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(swarmKey);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * HAP HTTP API Service Implementation
 * Uses fetch API for HTTP calls with swarm key authentication
 */
export class HapApiServiceImpl implements HapApiService {
  private readonly log;

  constructor(logger: LoggerService) {
    this.log = logger.createLogger({ namespace: 'six3ro:hap-api' });
  }

  async announceFile(
    config: HapApiConfig,
    request: FileAnnouncementRequest
  ): Promise<FileAnnouncementResponse> {
    const { hapApiUrl, swarmKey } = config;

    this.log.info('Announcing file via HAP HTTP API', {
      cid: request.cid,
      fileName: request.metadata.name,
      sixId: request.sixId,
      hapApiUrl
    });

    // Generate auth token from swarm key
    const authToken = await generateAuthToken(swarmKey);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => { controller.abort(); }, ANNOUNCE_TIMEOUT_MS);

    try {
      const response = await fetch(`${hapApiUrl}/api/v1/announce-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        this.log.error('HAP HTTP API error response', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`HAP API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json() as FileAnnouncementResponse;

      this.log.info('File announcement accepted by HAP', {
        cid: result.cid,
        accepted: result.accepted,
        message: result.message
      });

      return result;

    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        this.log.error('HAP HTTP API timeout', { cid: request.cid });
        throw new Error('HAP API request timed out');
      }

      this.log.error('HAP HTTP API request failed', {
        cid: request.cid,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  async healthCheck(hapApiUrl: string): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => { controller.abort(); }, HEALTH_CHECK_TIMEOUT_MS);

    try {
      const response = await fetch(`${hapApiUrl}/api/v1/health`, {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        this.log.debug('HAP health check passed', { hapApiUrl });
        return true;
      }

      this.log.warn('HAP health check failed', {
        hapApiUrl,
        status: response.status
      });
      return false;

    } catch (error) {
      clearTimeout(timeoutId);
      this.log.warn('HAP health check error', {
        hapApiUrl,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }
}
